import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import { CommonFunction } from "./common.function";
import moment from "moment";

export class ValidatorFunction {
  static notPositive(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (
        Validators.required(control) !== undefined &&
        Validators.required(control) !== null
      )
        return null;
      if (Math.sign(Number(control.value)) != 1) {
        return { notPositive: true };
      } else {
        return null;
      }
    };
  }

  static notNegative(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (
        Validators.required(control) !== undefined &&
        Validators.required(control) !== null
      )
        return null;
      if (Number(control.value) < 0) {
        return { notNegative: true };
      } else {
        return null;
      }
    };
  }

  static notDecimal(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (
        Validators.required(control) !== undefined &&
        Validators.required(control) !== null
      )
        return null;
      if (!Number.isInteger(Number(control.value))) {
        return { notDecimal: true };
      } else {
        return null;
      }
    };
  }

  static nricValidator() {
    return (control: AbstractControl) => {
      if (
        Validators.required(control) !== undefined &&
        Validators.required(control) !== null
      )
        return null;

      let value: string = control.value;
      if (value.length == 14) {
        value = value.replace("-", "");
        value = value.replace("-", "");
        value = value.replace("-", "");
      }
      if (value.length == 12) {
        let year = value.slice(0, 2);
        let month = value.slice(2, 4);
        let day = value.slice(4, 6);
        let strDate = `${year}-${month}-${day}`;
        if (moment(strDate, "YY-MM-DD").isValid()) {
          return null;
        }
      }

      return { nric: true };
    };
  }

  static maxGreaterThanMinValidator(minControlName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // Check if the control has a parent form group
      if (!control.parent) {
        return null;
      }

      // Get the minimum control from the parent form group
      const minControl = control.parent.get(minControlName);
      if (!minControl) {
        return null;
      }

      // Check if either the current control value or the minimum control value is null
      if (control.value === null || minControl.value === null) {
        return null;
      }

      // Check if the current control value is greater than the minimum control value
      if (+control.value > +minControl.value) {
        return null;
      }

      return { maxGreaterThanMin: true };
    };
  }

  static dateTimeOverlappingValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const startDate = control.get("startDate")?.value;
    const startTime = control.get("startTime")?.value;
    const endDate = control.get("endDate")?.value;
    const endTime = control.get("endTime")?.value;

    if (!startDate || !startTime || !endDate || !endTime) {
      return null;
    }

    const startDateTime = CommonFunction.combineDateTimeToUTCString(
      startDate,
      startTime
    );
    const endDateTime = CommonFunction.combineDateTimeToUTCString(
      endDate,
      endTime
    );

    if (new Date(startDateTime) >= new Date(endDateTime)) {
      return { dateTimeEqual: true };
    }

    return null;
  }

  static number(control: AbstractControl): ValidationErrors | null {
    if (
      control.value === null ||
      control.value === undefined ||
      control.value === ""
    ) {
      return null;
    }

    return /\D/.test(control.value) ? { number: true } : null;
  }

  static urlValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null; // Allow empty values if optional

    const pattern =
      /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/;
    return pattern.test(control.value) ? null : { invalidUrl: true };
  }

  static richTextRequiredValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const value = control.value || "";

    // Remove HTML tags and decode common entities
    const stripped = value
      .replace(/<[^>]*>/g, "") // remove HTML tags
      .replace(/&nbsp;/gi, "") // remove &nbsp;
      .replace(/\s/g, "") // remove all whitespace characters
      .trim();

    return stripped.length > 0 ? null : { required: true };
  }

  static alphabetsOnly(control: AbstractControl): ValidationErrors | null {
    if (
      control.value === null ||
      control.value === undefined ||
      control.value === ""
    ) {
      return null;
    }

    return /^[A-Za-z ]+$/.test(control.value) ? null : { alphabetsOnly: true };
  }

  /**
   * For a FormArray of emails (e.g. chip list inputs): requires at least
   * `min` entries that are non-empty and in a valid email format.
   */
  static minValidEmails(min = 1): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const values: unknown[] = Array.isArray(control.value)
        ? control.value
        : [];

      const validEmailCount = values.filter((value) => {
        if (typeof value !== "string" || !value.trim()) return false;
        // Validators.email only reads `value` off the control.
        return (
          Validators.email({
            value: value.trim()
          } as unknown as AbstractControl) === null
        );
      }).length;

      return validEmailCount >= min ? null : { minValidEmails: { min } };
    };
  }

  static fileSize(maxSizeMB: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const files: File[] = control.value;
      if (!files || files.length === 0) {
        return null;
      }

      const maxSize = maxSizeMB * 1024 * 1024;
      const invalidFile = files.find((file) => file.size > maxSize);

      return invalidFile ? { fileSize: true } : null;
    };
  }
}

export function notNullValidator(
  control: AbstractControl
): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === undefined) return { required: true };
  if (typeof value === "string" && value.trim() === "")
    return { required: true };
  return null; // 0 is valid, "0" is valid
}
